import { stableStringify } from '@cotera/utilities';
import type { JsonValue } from '../serializable';

/**
 * The canonical, org-scoped identity of a trigger. Two triggers with the same
 * identity on the same org ARE the same trigger and should collapse to a single
 * row (that is the whole point of trigger de-duplication).
 *
 * `null` means "not de-dupable" — always create a fresh trigger. A webhook
 * provider opts out of dedup by returning `null` from its identity projection.
 */
export type TriggerIdentity = Readonly<Record<string, JsonValue>> | null;

/**
 * Trigger kinds that participate in de-duplication. Matches the `kind`
 * discriminator of the `workflow_trigger_resource_index` view. `dataset`
 * triggers are intentionally excluded (separate polling subsystem).
 */
export type DedupableTriggerKind = 'cron' | 'webhook' | 'event';

/**
 * Stable string form of an identity, suitable for the `identity_key` column and
 * a `UNIQUE (owner_org_id, kind, identity_key)` constraint. Returns `null` for a
 * non-de-dupable identity, which callers persist as a NULL `identity_key` (NULLs
 * never collide under a unique index, so opted-out triggers stay distinct).
 *
 * Keys are sorted at every depth via {@link stableStringify}, so semantically
 * equal identities always produce the same string regardless of object key
 * order.
 */
export function triggerIdentityKey(identity: TriggerIdentity): string | null {
  return identity === null ? null : stableStringify(identity);
}

/**
 * Textual normalization of a cron expression for identity purposes: trims,
 * collapses whitespace, lower-cases (day/month names like `MON`/`JAN` are
 * case-insensitive), and pads a 5-field expression to the 6-field form the
 * scheduler uses (`0` seconds prefix) so `0 10 * * *` and `0 0 10 * * *` match.
 *
 * This is deliberately textual, not a full semantic canonicalization — e.g.
 * `* * *` vs an equivalent enumerated range are NOT unified. It catches the
 * common "same schedule typed slightly differently" cases; anything subtler
 * simply stays a distinct trigger, which is safe (never over-collapses).
 */
export function normalizeCronForIdentity(cron: string): string {
  const fields = cron.trim().toLowerCase().split(/\s+/);
  const padded = fields.length === 5 ? ['0', ...fields] : fields;
  return padded.join(' ');
}

/** Identity of a cron trigger: its normalized schedule + timezone. */
export function cronTriggerIdentity(params: {
  cron: string;
  timezone: string;
}): TriggerIdentity {
  return {
    cron: normalizeCronForIdentity(params.cron),
    timezone: params.timezone.trim(),
  };
}

/** Identity of an internal event trigger: the event name it listens on. */
export function eventTriggerIdentity(params: {
  eventName: string;
}): TriggerIdentity {
  return { eventName: params.eventName.trim() };
}

/**
 * Composes the identity of a webhook trigger from its provider, event, and the
 * provider-projected identity config.
 *
 * `identityConfig` is what the provider decided is identifying about the event
 * config:
 *  - an object (e.g. GitHub `{ owner, repo }`, or `{}` when the config is not
 *    identifying) → dedup on `(providerKey, eventKey, config)`
 *  - `null` → the provider opted out of dedup; the trigger is never de-duped.
 */
export function webhookTriggerIdentity(params: {
  providerKey: string;
  eventKey: string;
  identityConfig: JsonValue | null;
}): TriggerIdentity {
  if (params.identityConfig === null) {
    return null;
  }
  return {
    providerKey: params.providerKey,
    eventKey: params.eventKey,
    config: params.identityConfig,
  };
}
